import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import intonationThumbnail from '@/assets/intonation-thumbnail.jpg';
import nervousThumbnail from '@/assets/nervous-shaking-thumbnail.jpg';
import neurochemistryThumbnail from '@/assets/neurochemistry-thumbnail.jpg';

const BlogPreview: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const featuredArticles = [
    {
      title: "How to use intonation while presenting (TED talk Case Study)",
      excerpt: "Master the art of vocal variety to keep your audience engaged and make your presentations more impactful.",
      image: intonationThumbnail,
      category: "Video",
      slug: "how-to-use-intonation-when-presenting"
    },
    {
      title: "How to Prevent Nervous Shaking while presenting",
      excerpt: "Learn techniques to stay calm, composed, and in control during high-pressure presentations.",
      image: nervousThumbnail,
      category: "Video",
      slug: "how-to-prevent-nervous-shaking"
    },
    {
      title: "Neurochemistry of storytelling",
      excerpt: "Discover the science behind compelling narratives and how to use brain chemistry to captivate your audience.",
      image: neurochemistryThumbnail,
      category: "Video",
      slug: "neurochemistry-of-storytelling"
    }
  ];

  return (
    <section id="blog" className="py-20 bg-presintention-light section-padding" ref={sectionRef}>
      <div className="container-width">
        <div className="text-center mb-16">
          <span className={`inline-block px-3 py-1 bg-blue-50 text-presintention-accent rounded-full text-sm font-medium mb-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            Blog
          </span>
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
            Popular <span className="text-gradient">Articles and Videos</span>
          </h2>
          <p className={`max-w-2xl mx-auto text-presintention-muted text-lg transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
            Insights and strategies to elevate your presentation skills and executive presence.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {featuredArticles.map((article, index) => (
            <div
              key={index}
              className={`bg-white rounded-lg shadow-lg overflow-hidden hover-lift transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${(index + 3) * 100}ms` }}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.src = 'lovable-uploads/ce5735e2-b1ec-41e3-a57f-c730a06061c4.png';
                  }}
                />
                <span className="absolute top-4 left-4 px-3 py-1 bg-presintention-accent text-white text-xs font-medium rounded-full">
                  {article.category}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3 text-presintention-dark">
                  {article.title}
                </h3>
                <p className="text-presintention-muted mb-4">
                  {article.excerpt}
                </p>
                <Link to={`/blog/${article.slug}`} className="text-presintention-accent font-medium hover:underline inline-flex items-center">
                  Watch Video
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <Button asChild className="bg-presintention-accent text-white hover:bg-presintention-accent/90">
            <Link to="/blog">
              More
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
